// consentForm.js
// -----------------------------------------------------------------------------
// The installation consent a customer signs on the driver's phone BEFORE the
// team starts work in their home: the wording, the header it carries, and when
// it may be saved, as pure functions.
//
// It is taken on เริ่มติดตั้ง, ahead of the "before installation" photos, so the
// stamp never exists without it -- the same order every photo stage keeps. Two
// reasons pass it without a signature: ระบบขัดข้อง, and ลูกค้าไม่อยู่ -- the
// customer asked for the products to be left in the house (mostly online
// orders), and the photo stages then show the house, the spot and the products
// left there. A customer who is home and will not consent does not get the
// installation; the driver reports ส่งไม่สำเร็จ.
//
// The wording is versioned. Each saved consent records CONSENT_VERSION, so a
// signature can always be matched to the text the customer was shown. Change
// a word below, bump the version.
//
// The full terms and the privacy notice were drafted on 2026-10-03 and adopted
// as written by the business owner the same day. DRAFT is kept as a switch:
// set it true and both pop-ups say ฉบับร่าง, for a future revision under review.
//
// Pure: no Vue, no Firestore, CommonJS, so scripts/check-driver-day.js can
// require() it -- the same arrangement proofPhoto.js keeps.
// -----------------------------------------------------------------------------

const CONSENT_VERSION = '2026-10-03'
const DRAFT = false

const TITLE = 'ข้อตกลงการรับบริการติดตั้งฟรี'

// Part 2, the terms shown on the main screen. Short on purpose: the long text
// lives in FULL_TERMS, behind a link, so the screen stays readable at the door.
const TERMS = [
  { icon: 'mdi-tools', head: 'บริการติดตั้งฟรี', text: 'บริษัทให้บริการติดตั้งแก่ลูกค้าโดยไม่มีค่าใช้จ่าย' },
  {
    icon: 'mdi-office-building-outline',
    head: 'ขอบเขตความรับผิดชอบ',
    text: 'บริษัทขอสงวนสิทธิ์ไม่รับผิดชอบต่อความเสียหายของทรัพย์สินที่เกิดจากสภาพหน้างาน (เช่น ผนัง เพดาน พื้น) ' +
      'เว้นแต่ความเสียหายนั้นเกิดจากความประมาทเลินเล่ออย่างร้ายแรงของพนักงานติดตั้ง'
  },
  { icon: 'mdi-handshake-outline', head: 'ความร่วมมือ', text: 'ลูกค้าตกลงให้ความร่วมมือในการเตรียมพื้นที่เพื่อให้งานติดตั้งเสร็จสมบูรณ์' },
  { icon: 'mdi-script-text-outline', head: 'เงื่อนไขอื่นๆ', text: 'เป็นไปตามที่บริษัทกำหนด' }
]

// Part 3, the box the customer ticks before signing.
const CONSENT_TEXT = 'ข้าพเจ้าได้ตรวจสอบสภาพสินค้า อ่านและยอมรับข้อตกลงข้างต้น และยินยอมให้บริษัทเก็บรวบรวม' +
  'ข้อมูลส่วนบุคคล (เช่น ลายเซ็น ภาพถ่ายหน้างาน) เพื่อใช้ในการให้บริการและรับประกันสินค้า ตามนโยบายความเป็นส่วนตัว'

const FULL_TERMS = [
  '1. บริษัทให้บริการติดตั้งสินค้าที่ลูกค้าซื้อจากบริษัทโดยไม่มีค่าใช้จ่าย ณ สถานที่ที่ระบุในใบสั่งขาย ' +
    'ภายในวันและช่วงเวลาที่นัดหมาย',
  '2. ลูกค้าหรือผู้แทนต้องอยู่ ณ สถานที่ติดตั้งตลอดระยะเวลาการติดตั้ง และจัดเตรียมพื้นที่ให้ว่าง ' +
    'สะอาด และเข้าถึงได้ รวมถึงขนย้ายทรัพย์สินที่อาจเสียหายออกจากบริเวณที่ติดตั้ง',
  '3. บริษัทไม่รับผิดชอบความเสียหายที่เกิดจากสภาพหน้างาน เช่น ผนังที่ไม่แข็งแรง ท่อน้ำหรือสายไฟ' +
    'ที่ซ่อนอยู่ในผนังหรือพื้นโดยไม่ได้แจ้งให้ทราบ พื้นที่ไม่ได้ระดับ เว้นแต่เกิดจากความประมาทเลินเล่อ' +
    'อย่างร้ายแรงของพนักงานติดตั้ง',
  '4. งานนอกเหนือจากการติดตั้งตามมาตรฐาน เช่น การเจาะหรือดัดแปลงโครงสร้าง การเดินสายไฟหรือท่อ ' +
    'ไม่รวมอยู่ในบริการติดตั้งฟรี',
  '5. หากพบว่าไม่สามารถติดตั้งได้เนื่องจากสภาพหน้างาน พนักงานจะแจ้งลูกค้าและบันทึกเหตุผลไว้ ' +
    'โดยบริษัทจะติดต่อเพื่อนัดหมายใหม่',
  '6. การรับประกันสินค้าเป็นไปตามเงื่อนไขการรับประกันของสินค้าแต่ละรายการ',
  '7. เงื่อนไขอื่นใดที่มิได้ระบุไว้ เป็นไปตามที่บริษัทกำหนด'
]

const PRIVACY_NOTICE = [
  'บริษัทเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคลของท่านตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562',
  'ข้อมูลที่เก็บ: ชื่อ ที่อยู่ หมายเลขโทรศัพท์ ลายเซ็น ภาพถ่ายสถานที่ติดตั้งและงานที่ติดตั้ง ' +
    'และตำแหน่งที่ตั้งขณะพนักงานบันทึกงาน',
  'วัตถุประสงค์: เพื่อจัดส่งและติดตั้งสินค้า ยืนยันการรับบริการ ให้บริการหลังการขาย และรับประกันสินค้า',
  'ระยะเวลาการเก็บ: ตลอดระยะเวลารับประกันสินค้า และตามที่กฎหมายกำหนด',
  'สิทธิของท่าน: ท่านมีสิทธิขอเข้าถึง แก้ไข ลบ หรือคัดค้านการประมวลผลข้อมูลส่วนบุคคลของท่าน ' +
    'โดยติดต่อฝ่ายบริการลูกค้าของบริษัท'
]

// The reasons a consent may be saved without a signature. Deliberately not
// "the customer refused": a refusal ends the visit as ส่งไม่สำเร็จ.
const ABSENT = 'ลูกค้าไม่อยู่'
const SKIP_REASONS = [ABSENT, 'ระบบขัดข้อง']

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// '2026-10-03' -> '3 ต.ค. 2569'; '' for anything else.
function thaiDate (iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
  if (!m) return ''
  const month = Number(m[2])
  if (month < 1 || month > 12) return ''
  return Number(m[3]) + ' ' + TH_MONTHS[month - 1] + ' ' + (Number(m[1]) + 543)
}

// Part 1: the date, the documents and the customer. A stop carries no invoice
// number, so the SOs it delivers stand for the document -- every one of them,
// once each, for a customer with two orders at one visit.
function headerFor (stop) {
  const s = stop || {}
  const docs = []
  ;(s.lines || []).forEach(l => {
    const no = l && l.soNo ? String(l.soNo) : ''
    if (no && docs.indexOf(no) < 0) docs.push(no)
  })
  return {
    date: thaiDate(s.deliveryDate),
    docNo: docs.join(', '),
    custName: s.custName || ''
  }
}

// Why the consent may not be saved yet, in the Thai the screen shows, or ''.
// A signature needs the box ticked; without one, only SKIP_REASONS will do.
function consentProblem (input) {
  const i = input || {}
  if (i.hasSignature) {
    if (!i.accepted) return 'ให้ลูกค้าติ๊กยอมรับข้อตกลงก่อนเซ็นชื่อ'
    return ''
  }
  if (SKIP_REASONS.indexOf(i.skippedReason) >= 0) return ''
  if (!i.accepted) return 'ให้ลูกค้าติ๊กยอมรับข้อตกลงและเซ็นชื่อ'
  return 'ให้ลูกค้าเซ็นชื่อก่อนเริ่มติดตั้ง'
}

// Whether this stop's consent is already saved -- signed, or skipped for a
// recorded reason. A reopened เริ่มติดตั้ง then goes straight to the photos
// rather than handing the phone back to a customer who has signed.
function consentDone (stop) {
  const proof = (stop && stop.proof) || {}
  return !!(proof.consentSignatureUrl || proof.consentSkippedReason)
}

// Nobody was home: the consent was passed as ลูกค้าไม่อยู่. ปิดงาน then starts
// its signature reason on the same answer, and the monitor board flags the stop
// so staff can call the customer to confirm the drop-off.
function customerAbsent (stop) {
  const proof = (stop && stop.proof) || {}
  return proof.consentSkippedReason === ABSENT
}

// The name ปิดงาน's ชื่อผู้เซ็น starts with, still editable: whoever signed the
// consent -- usually the same person at the end of the job, and if a family
// member signed, it is their name rather than the account holder's -- else the
// order's customer, for a consent skipped as ระบบขัดข้อง or a stop installed
// before consent existed. Empty when nobody was home: a customer's name beside
// a missing signature would read as though they had signed.
function signerDefault (stop) {
  const s = stop || {}
  if (customerAbsent(s)) return ''
  const proof = s.proof || {}
  return String(proof.consentSignedName || '').trim() || String(s.custName || '').trim()
}

// The fields saveConsent writes under deliveryStop.proof, minus the server
// timestamp and the driver's uid, which only the service can add. A skipped
// consent records no name and claims no acceptance.
function consentFields (input) {
  const i = input || {}
  const signed = !!i.signatureUrl
  return {
    consentSignatureUrl: signed ? i.signatureUrl : '',
    consentSignedName: signed ? String(i.signedName || '').trim() : '',
    consentAccepted: signed && !!i.accepted,
    consentSkippedReason: signed ? '' : (i.skippedReason || ''),
    consentVersion: CONSENT_VERSION,
    consentAtDevice: i.atDevice || ''
  }
}

// ใบยินยอมรับบริการติดตั้ง is about an installation. A return-only pickup has
// none, so เริ่มรับคืน goes straight to its photos. An exchange installs the new
// items and still asks.
function consentRequired (stop) {
  return !(stop && stop.returnOnly)
}

module.exports = {
  CONSENT_VERSION,
  DRAFT,
  TITLE,
  TERMS,
  CONSENT_TEXT,
  FULL_TERMS,
  PRIVACY_NOTICE,
  ABSENT,
  SKIP_REASONS,
  thaiDate,
  headerFor,
  consentProblem,
  consentDone,
  consentRequired,
  customerAbsent,
  signerDefault,
  consentFields
}
